import React, { useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    List,
    Row,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    Upload,
    message,
} from "antd";
import {
    CloudUploadOutlined,
    FileExcelOutlined,
    FileZipOutlined,
} from "@ant-design/icons";

import { runSetupImport } from "../../api/setupImportApi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Dragger } = Upload;
const { Title, Text } = Typography;

/**
 * TEMPORARY admin-only page for the one-off initial data load.
 *
 * Delete this page together with config/setupImport.js, api/setupImportApi.js, its route and its
 * sidebar entry once the final data has been imported.
 */
function SetupImportPage() {
    const { t } = useTranslation();

    const [excelFile, setExcelFile] = useState(null);
    const [zipFiles, setZipFiles] = useState([]);
    const [replaceExisting, setReplaceExisting] = useState(true);
    const [requireInvoice, setRequireInvoice] = useState(true);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    const handleRun = async () => {
        if (!excelFile) {
            message.error(t("setup.needExcel"));
            return;
        }

        setRunning(true);
        setError("");
        setResult(null);

        try {
            const data = await runSetupImport({
                excelFile,
                zipFiles,
                replaceExisting,
                requireInvoice,
            });

            if (data?.success) {
                setResult(data);
                message.success(data.message || t("setup.done"));
            } else {
                const text = data?.message || t("setup.failed");
                setError(text);
                message.error(text);
            }
        } catch (err) {
            const text = err?.message || t("setup.failed");
            setError(text);
            message.error(text);
        } finally {
            setRunning(false);
        }
    };

    const excelProps = {
        multiple: false,
        accept: ".xlsx,.xls",
        showUploadList: false,
        beforeUpload: (file) => {
            setExcelFile(file);
            setResult(null);
            return false;
        },
    };

    const zipProps = {
        multiple: true,
        accept: ".zip",
        showUploadList: false,
        beforeUpload: (file) => {
            // antd calls this once per file; collect them all, skipping repeats.
            setZipFiles((prev) =>
                prev.some((item) => item.name === file.name && item.size === file.size)
                    ? prev
                    : [...prev, file]
            );
            setResult(null);
            return false;
        },
    };

    const sheetColumns = [
        { title: t("setup.col.sheet"), dataIndex: "sheetName", key: "sheetName" },
        { title: t("setup.col.rows"), dataIndex: "totalRows", key: "totalRows", align: "right" },
        {
            title: t("setup.col.imported"),
            dataIndex: "importedRows",
            key: "importedRows",
            align: "right",
        },
        {
            title: t("setup.col.invalid"),
            dataIndex: "invalidRows",
            key: "invalidRows",
            align: "right",
            render: (value) => (value ? <Text type="danger">{value}</Text> : 0),
        },
        {
            title: t("setup.col.invoices"),
            dataIndex: "matchedInvoices",
            key: "matchedInvoices",
            align: "right",
        },
        {
            title: t("setup.col.skipped"),
            dataIndex: "skippedNoInvoice",
            key: "skippedNoInvoice",
            align: "right",
            render: (value) => (value ? <Text type="warning">{value}</Text> : 0),
        },
    ];

    const invalidColumns = [
        { title: t("setup.col.sheet"), dataIndex: "sheetName", key: "sheetName", width: 160 },
        { title: t("col.row"), dataIndex: "rowNumber", key: "rowNumber", width: 80 },
        { title: t("setup.col.code"), dataIndex: "code", key: "code", width: 100 },
        { title: t("setup.col.reason"), dataIndex: "reason", key: "reason" },
    ];

    return (
        <div className="space-y-4">
            <Card bordered={false} className="shadow-sm rounded-2xl">
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={16}>
                        <Space direction="vertical" size={4}>
                            <Space>
                                <Title level={2} style={{ margin: 0 }}>
                                    {t("setup.title")}
                                </Title>
                                <Tag color="orange">{t("setup.temporary")}</Tag>
                            </Space>
                            <Text type="secondary">{t("setup.subtitle")}</Text>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card bordered={false} className="shadow-sm rounded-2xl">
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <Dragger {...excelProps}>
                            <p className="ant-upload-drag-icon">
                                <FileExcelOutlined />
                            </p>
                            <p className="ant-upload-text">{t("setup.excelDrop")}</p>
                            <p className="ant-upload-hint">{t("setup.excelHint")}</p>
                        </Dragger>
                        <div className="mt-2">
                            {excelFile ? (
                                <Tag color="green">{excelFile.name}</Tag>
                            ) : (
                                <Tag>{t("excel.noFile")}</Tag>
                            )}
                        </div>
                    </Col>

                    <Col xs={24} md={12}>
                        <Dragger {...zipProps}>
                            <p className="ant-upload-drag-icon">
                                <FileZipOutlined />
                            </p>
                            <p className="ant-upload-text">{t("setup.zipDrop")}</p>
                            <p className="ant-upload-hint">{t("setup.zipHint")}</p>
                        </Dragger>
                        <div className="mt-2">
                            {zipFiles.length ? (
                                <Space size={[4, 4]} wrap>
                                    {zipFiles.map((file) => (
                                        <Tag
                                            key={`${file.name}-${file.size}`}
                                            color="green"
                                            closable
                                            onClose={() =>
                                                setZipFiles((prev) =>
                                                    prev.filter((item) => item !== file)
                                                )
                                            }
                                        >
                                            {file.name}
                                        </Tag>
                                    ))}
                                    <Tag color="blue">{`${zipFiles.length} ZIP`}</Tag>
                                </Space>
                            ) : (
                                <Tag>{t("excel.noFile")}</Tag>
                            )}
                        </div>
                    </Col>
                </Row>

                <Divider />

                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    <Space wrap align="center">
                        <Switch checked={replaceExisting} onChange={setReplaceExisting} />
                        <Text>{t("setup.replaceExisting")}</Text>
                        <Text type="secondary">{t("setup.replaceHint")}</Text>
                    </Space>

                    <Space wrap align="center">
                        <Switch checked={requireInvoice} onChange={setRequireInvoice} />
                        <Text>{t("setup.requireInvoice")}</Text>
                        <Text type="secondary">{t("setup.requireInvoiceHint")}</Text>
                    </Space>
                </Space>

                <div className="mt-4">
                    <Button
                        type="primary"
                        size="large"
                        icon={<CloudUploadOutlined />}
                        loading={running}
                        disabled={!excelFile}
                        onClick={handleRun}
                    >
                        {t("setup.run")}
                    </Button>
                </div>

                {error && (
                    <Alert
                        className="mt-4"
                        type="error"
                        showIcon
                        message={t("setup.failed")}
                        description={error}
                    />
                )}
            </Card>

            {result && (
                <Card bordered={false} className="shadow-sm rounded-2xl">
                    <Space direction="vertical" size={16} style={{ width: "100%" }}>
                        <Alert type="success" showIcon message={t("setup.done")} description={result.message} />

                        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
                            <Descriptions.Item label={t("setup.stat.totalRows")}>
                                {result.totalExcelRows}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.imported")}>
                                {result.importedRows}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.invalid")}>
                                {result.invalidRows}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.pdfs")}>
                                {result.totalPdfFiles}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.matched")}>
                                {result.matchedInvoices}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.withoutInvoice")}>
                                {result.rowsWithoutInvoice}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.skippedNoInvoice")}>
                                {result.skippedNoInvoice}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.unmatchedPdfs")}>
                                {result.unmatchedPdfFiles}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.ambiguous")}>
                                {result.ambiguousMatches}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("setup.stat.deleted")}>
                                {result.deletedExistingRows}
                            </Descriptions.Item>
                        </Descriptions>

                        <div>
                            <Title level={5}>{t("setup.perSheet")}</Title>
                            <Table
                                rowKey="sheetIndex"
                                size="small"
                                bordered
                                pagination={false}
                                columns={sheetColumns}
                                dataSource={result.sheets || []}
                            />
                        </div>

                        {result.invalidRowDetails?.length > 0 && (
                            <div>
                                <Title level={5}>
                                    {t("setup.invalidRows")} ({result.invalidRowDetails.length})
                                </Title>
                                <Table
                                    rowKey={(r) => `${r.sheetName}-${r.rowNumber}`}
                                    size="small"
                                    bordered
                                    columns={invalidColumns}
                                    dataSource={result.invalidRowDetails}
                                    pagination={{ pageSize: 10, showSizeChanger: true }}
                                />
                            </div>
                        )}

                        {result.skippedNoInvoiceDetails?.length > 0 && (
                            <div>
                                <Title level={5}>
                                    {t("setup.skippedNoInvoice")} (
                                    {result.skippedNoInvoiceDetails.length})
                                </Title>
                                <Table
                                    rowKey={(r) => `noinv-${r.sheetName}-${r.rowNumber}`}
                                    size="small"
                                    bordered
                                    columns={invalidColumns}
                                    dataSource={result.skippedNoInvoiceDetails}
                                    pagination={{ pageSize: 10, showSizeChanger: true }}
                                />
                            </div>
                        )}

                        {result.unmatchedPdfDetails?.length > 0 && (
                            <div>
                                <Title level={5}>
                                    {t("setup.unmatchedPdfs")} ({result.unmatchedPdfDetails.length})
                                </Title>
                                <List
                                    size="small"
                                    bordered
                                    dataSource={result.unmatchedPdfDetails}
                                    renderItem={(item) => <List.Item>{item}</List.Item>}
                                />
                            </div>
                        )}

                        {result.ambiguousMatchDetails?.length > 0 && (
                            <div>
                                <Title level={5}>
                                    {t("setup.ambiguous")} ({result.ambiguousMatchDetails.length})
                                </Title>
                                <List
                                    size="small"
                                    bordered
                                    dataSource={result.ambiguousMatchDetails}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <Text type="warning">{item}</Text>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )}
                    </Space>
                </Card>
            )}
        </div>
    );
}

export default SetupImportPage;
